import React, { useState, useEffect } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import { validateToken, callApi } from "../../../lib/utils";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChartSimple,
} from "@fortawesome/free-solid-svg-icons";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as ChartTooltip,
    Legend,
    ResponsiveContainer,
    ReferenceArea,
    ReferenceLine
} from 'recharts';
import moment from "moment";
import GenericModal from "../../GenericComponents/GenericModal/GenericModal";
import HRMEventGraph from "../../GenericComponents/EventSpecificGraph/HRMEventGraph";
import { PulseLoader } from 'react-spinners'
import "./HRM.css";

const formatEventDuration = (totalSeconds) => {
    if (totalSeconds >= 60) {
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds - minutes * 60;
        return `${minutes} min ${seconds} secs`;
    }
    return `${totalSeconds} seconds`;
};

const formatModalTitle = (totalSeconds) =>
    `Heart Rate Monitoring ( Duration : ${formatEventDuration(totalSeconds)} )`;

const useViewportWidth = () => {
    const [width, setWidth] = useState(
        typeof window !== "undefined" ? window.innerWidth : 1024
    );

    useEffect(() => {
        const handleResize = () => setWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return width;
};

const HRMList = ({ ...props }) => {
    const [showGraphModal, setShowGraphModal] = useState(false);
    const [graphLoader, setGraphLoader] = useState(false);
    const [graphMomdalData, setGraphModalData] = useState({});
    const [modalData, setModalData] = useState({});
    const viewportWidth = useViewportWidth();
    const isCompactLabels = viewportWidth < 768;
    const isMobileView = viewportWidth < 768;

    const handleShowGraphModal = (show, data) => {
        if (show) {
            // 1. Set loader to true immediately to prevent "No Data" flash
            setGraphLoader(true);

            validateToken((err) => {
                if (err) {
                    // 2. Ensure loader is turned off if validation fails
                    setGraphLoader(false);
                    window.location = "/logout";
                    return;
                }

                // 3. Fetch data
                callApi(
                    `/api/event/hrm/sensor-data/${data.id}`,
                    {},
                    "GET"
                )
                    .then((response) => {
                        setGraphModalData(response?.data?.data);
                        setGraphLoader(false);
                    })
                    .catch((err) => {
                        console.log("err: ", err);
                        setGraphLoader(false);
                    });
            });
        } else {
            setGraphModalData({});
        }
        setModalData(data);
        setShowGraphModal(show);
    };

    const srNumberFormatter = (cell, row, rowIndex, formatExtraData) => {
        if (props.currentPage > 1)
            return <>{props.perPage * (props.currentPage - 1) + (rowIndex + 1)}</>;
        else return <>{rowIndex + 1}</>;
    };

    const nameFormatter = (cell, row) => {
        return (
            <>
                <div>
                    {" "}
                    {`${cell.firstName !== null ? cell.firstName : ""} ${cell.lastName !== null ? cell.lastName : ""
                        }`}
                </div>
            </>
        );
    };

    const dateFormatter = (cell, row) => {
        return `${moment(row.eventTimestamp - row.totalEventDuration * 1000).format("MM/DD/YYYY")}  
        ${moment(row.eventTimestamp - row.totalEventDuration * 1000).format("hh:mm A")}`;
    };

    const durationFormatter = (cell, row) => {
        return (
            row?.totalEventDuration >= 60
                ? `${Math.floor(row?.totalEventDuration / 60)} min ${row?.totalEventDuration - Math.floor(row?.totalEventDuration / 60) * 60
                } secs`
                : `${row?.totalEventDuration} seconds`
        );
    };

    const heartRateFormatter = (cell, row) => {
        return `${cell} bpm`;
    };

    const graphFormatter = (cell, row) => {
        return (
            <OverlayTrigger
                placement="top"
                overlay={
                    <Tooltip id="button-tooltip-location">
                        Click To View Event Graph
                    </Tooltip>
                }
            >
                <div className="graph-content">
                    <FontAwesomeIcon icon={faChartSimple} style={{ cursor: "pointer" }} />
                </div>
            </OverlayTrigger>
        );
    };

    const addressFormatter = (cell) => {
        if (!cell) return "-";
        return (
            <span className="hrm-address-text" title={cell}>
                {cell}
            </span>
        );
    };

    const columns = [
        {
            dataField: "id",
            text: "No.",
            formatter: srNumberFormatter,
            headerStyle: () => {
                return { width: "48px" };
            },
        },
        {
            dataField: "eventTimestamp",
            text: "EVENT DATE/TIME",
            formatter: dateFormatter,
        },
        // {
        //     dataField: "eventSubType",
        //     text: "EVENT TYPE",
        // },
        {
            dataField: "minHeartRate",
            text: "MIN HEART RATE",
            formatter: heartRateFormatter,
        },
        {
            dataField: "maxHeartRate",
            text: "MAX HEART RATE",
            formatter: heartRateFormatter,
        },
        {
            dataField: "avgHeartRate",
            text: "AVG HEART RATE",
            formatter: heartRateFormatter,
        },
        {
            dataField: "belowLowerThresholdCount",
            text: "LOWER THRESHOLD COUNT (>=)",
        },
        {
            dataField: "aboveUpperThresholdCount",
            text: "UPPER THRESHOLD COUNT (<=)",
        },
        {
            dataField: "totalEventDuration",
            text: "TOTAL DURATION",
            formatter: durationFormatter
        },
        {
            dataField: "eventGraph",
            text: "EVENT GRAPH",
            formatter: graphFormatter,
            // headerStyle: () => {
            //     return { width: "164px" };
            // },
            events: {
                onClick: (e, column, columnIndex, row, rowIndex) => {
                    handleShowGraphModal(true, row);
                },
            },
            id: "graphButton",
        },
        {
            dataField: "address",
            text: "ADDRESS",
            formatter: addressFormatter,
            headerStyle: () => ({ minWidth: "200px" }),
            style: () => ({ minWidth: "200px", maxWidth: "280px" }),
            classes: "hrm-address-cell",
        },
    ];

    const defaultSorted = [
        {
            dataField: "startDate",
            order: "desc",
        },
    ];

    const createHTMLTemplateForToolip = (data) => {
        let html =
            "<div style='width: 500px'><center><p><strong>Heart Rate: " +
            data.min +
            "</strong></p><p><strong>";

        html += "</p></center></div>";
        return html;
    };

    const AreaGraph = (props) => {
        const result = props.graphData?.processedHeartRateData?.filter((item) => {
            return item.min !== 0 && item.max !== 0 && item.avg !== 0;
        });
        let maxHeartRate = result && Math.max(...result.map(o => o.max));

        let newData = [];
        if (result !== undefined) {
            newData = result?.map((item, index) => {
                let sec = moment(item.timestamp).format("hh:mm");
                let min = item.min;
                let max = item.max;
                let avg = item.avg;
                let lowThreshold = modalData.hrmSensorSetting.lowHRThreshold;
                let highThreshold = modalData.hrmSensorSetting.highHRThreshold;
                let avgThreshold = highThreshold - lowThreshold;
                let customTooltip = createHTMLTemplateForToolip(item);

                return [sec, min, max, avg, lowThreshold, avgThreshold, highThreshold, customTooltip];
            });
        }

        const paramData = ["Min Heart Rate", "Max Heart Rate", "Avg Heart Rate", "Low HR Threshold", "Avg HR Threshold", "High HR Threshold"];

        const data = [
            [
                { type: "string", label: "Heart Rate Duration ( In Secs )" },
                ...paramData,
                {
                    role: "tooltip",
                    calc: "stringify",
                    type: "string",
                    color: "#212121",
                    p: { html: true }
                },
            ],
            ...newData,
        ];

        return (
            <HRMEventGraph
                data={data}
                loading={graphLoader}
                maxHeartRate={maxHeartRate}
                modalData={modalData}
                eventData={newData}
                taskEvent={false}
            />
        );
    };

    const NewHeartRateChart = (props) => {
        const result = props.graphData?.rawHeartRateData?.filter((item) => {
            return item.value !== 0;
        });

        let lowThreshold = modalData?.hrmSensorSetting?.lowHRThreshold;
        let highThreshold = modalData?.hrmSensorSetting?.highHRThreshold;

        const formatTime = (timestampMs) => {
            if (!timestampMs) return '';
            const date = new Date(timestampMs);
            let hours = date.getHours();
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12;
            hours = hours ? hours : 12;
            const formattedHours = String(hours).padStart(2, '0');
            return `${formattedHours}:${minutes} ${ampm}`;
        };

        // 1. PREPARE DATA FIRST so we know the start time
        let chartData = [];
        if (result !== undefined) {
            chartData = [...result];
        }

        // 2. CALCULATE TIMESTAMPS
        let eventStartTime = (modalData?.eventTimestamp - (modalData?.totalEventDuration * 1000));
        let eventEndTime = modalData?.eventTimestamp;

        // Get the very first data timestamp (if data exists)
        let dataMin = chartData.length > 0 ? chartData[0].timestamp : null;
        let dataMax = chartData.length > 0 ? chartData[chartData.length - 1].timestamp : null;

        // 👇 --- LOGIC: CLAMP ELEVATED HR ---
          // HTE (high) uses highHRThresholdDuration; otherwise use lowHRThresholdDuration (minutes)
          const elevatedHRDurationMinutes =
            modalData?.eventSubType === "HTE"
              ? modalData?.hrmSensorSetting?.highHRThresholdDuration
              : modalData?.hrmSensorSetting?.lowHRThresholdDuration;
          let calculatedElevatedHR =
            eventStartTime - elevatedHRDurationMinutes * 60 * 1000;
          let elevatedHR = calculatedElevatedHR;

        // If data starts LATER than the 4-minute mark, snap the line to the Data Start
        if (dataMin && dataMin > calculatedElevatedHR) {
            elevatedHR = dataMin;
        }
        // 👆 ---------------------------------


        // 3. CALCULATE DOMAIN (with Padding)
        // We use the new 'elevatedHR' variable here
        const allValidTimestamps = [elevatedHR, eventStartTime, eventEndTime, dataMin, dataMax].filter(
            (ts) => typeof ts === 'number' && !isNaN(ts)
        );

        let finalDomainMin = 'dataMin';
        let finalDomainMax = 'dataMax';

        if (allValidTimestamps.length > 0) {
            const minTimestamp = Math.min(...allValidTimestamps);
            const maxTimestamp = Math.max(...allValidTimestamps);

            // Keep the padding so labels don't get cut off
            finalDomainMin = minTimestamp - 60000;
            finalDomainMax = maxTimestamp + 60000;

        } else if (chartData.length > 0) {
            finalDomainMin = dataMin - 60000;
            finalDomainMax = dataMax + 60000;
        }

        // 4. GENERATE TICKS (Unchanged)
        const generateTicks = (minDomain, maxDomain) => {
            if (typeof minDomain !== 'number' || typeof maxDomain !== 'number' || isNaN(minDomain) || isNaN(maxDomain)) {
                return [];
            }
            const ticks = [];
            let currentTick = new Date(minDomain);
            currentTick.setSeconds(0, 0);

            if (currentTick.getTime() < minDomain) {
                currentTick.setMinutes(currentTick.getMinutes() + 1);
            }

            while (currentTick.getTime() <= maxDomain) {
                ticks.push(currentTick.getTime());
                currentTick.setMinutes(currentTick.getMinutes() + 1);
            }
            return ticks;
        };

        const customTicks = generateTicks(finalDomainMin, finalDomainMax);
        const chartHeight =
            viewportWidth >= 768 ? 400
            : viewportWidth >= 576 ? 280
            : 240;
        const chartMargin = isMobileView
            ? { top: 16, right: 8, left: 0, bottom: 0 }
            : { top: 30, right: 30, left: 20, bottom: 5 };

        return (
            <div className="hrm-graph-modal-wrapper">
                <div className="hrm-graph-modal-card">
                <h5 className="hrm-graph-modal__title">
                    {formatModalTitle(modalData?.totalEventDuration || 0)}
                </h5>

                {graphLoader && (
                    <div className="hrm-graph-modal__loader">
                        <div className="pulse-loader-container inside-graph">
                            <PulseLoader
                                color={"#70757a"}
                                loading={graphLoader}
                                aria-label="Loading Spinner"
                                data-testid="loader"
                            />
                        </div>
                    </div>
                )}

                {(!graphLoader && chartData.length === 0) && (
                    <div className="hrm-graph-modal__empty">No heart rate data available.</div>
                )}

                {(!graphLoader && chartData.length > 0) && (
                    <>
                        <div className="hrm-chart-container">
                            <ResponsiveContainer width="100%" height={chartHeight}>
                                <LineChart
                                    data={chartData}
                                    margin={chartMargin}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />

                                    <XAxis
                                        dataKey="timestamp"
                                        tickFormatter={formatTime}
                                        type="number"
                                        domain={[finalDomainMin, finalDomainMax]}
                                        scale="time"
                                        tick={{ fontSize: isMobileView ? 10 : 12 }}
                                        interval="preserveStartEnd"
                                        label={
                                            isMobileView
                                                ? undefined
                                                : { value: 'Time (Minutes)', position: 'bottom', offset: 0, dy: 10 }
                                        }
                                        ticks={customTicks}
                                    />

                                    <YAxis
                                        width={isMobileView ? 36 : 60}
                                        tick={{ fontSize: isMobileView ? 10 : 12 }}
                                        label={
                                            isMobileView
                                                ? undefined
                                                : { value: 'Heart Rate (BPM)', angle: -90, position: 'insideLeft' }
                                        }
                                    />

                                    <ChartTooltip
                                        formatter={(value) => [`${value} BPM`, 'Heart Rate']}
                                        labelFormatter={(label) => `Time: ${formatTime(label)}`}
                                    />

                                    <Legend
                                        verticalAlign="top"
                                        align={isMobileView ? "center" : "right"}
                                        wrapperStyle={{
                                            paddingBottom: isMobileView ? 12 : 30,
                                            fontSize: isMobileView ? '11px' : '14px',
                                            color: '#555',
                                        }}
                                        iconType="circle"
                                        iconSize={8}
                                    />

                                    <Line
                                        type="monotone"
                                        dataKey="value"
                                        name="Heart Rate"
                                        stroke="red"
                                        activeDot={{ r: isMobileView ? 4 : 6 }}
                                        dot={false}
                                        strokeWidth={isMobileView ? 2 : 3}
                                    />

                                    <ReferenceArea
                                        y1={lowThreshold}
                                        y2={highThreshold}
                                        stroke="#82ca9d"
                                        fill="#82ca9d"
                                        fillOpacity={0.2}
                                    />

                                    <ReferenceLine
                                        x={elevatedHR}
                                        stroke="black"
                                        strokeDasharray="3 3"
                                        label={{
                                            value:'Elevated HR',
                                            position: 'top',
                                            fill: 'black',
                                            fontSize: isCompactLabels ? 10 : 12,
                                            dy: -5,
                                        }}
                                    />
                                    <ReferenceLine
                                        x={eventStartTime}
                                        stroke="blue"
                                        strokeDasharray="3 3"
                                        label={{
                                            value:'HR Start',
                                            position: 'top',
                                            fill: 'blue',
                                            fontSize: isCompactLabels ? 10 : 12,
                                            dy: 10,
                                        }}
                                    />
                                    <ReferenceLine
                                        x={eventEndTime}
                                        stroke="blue"
                                        strokeDasharray="3 3"
                                        label={{
                                            value:'HR End',
                                            position: 'top',
                                            fill: 'blue',
                                            fontSize: isCompactLabels ? 10 : 12,
                                            dy: -10,
                                        }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                        <p className="hrm-graph-modal__axis-label">Time(Minutes)</p>
                        <div className="hrm-graph-stats">
                            <div className="hrm-graph-stat">
                                <span className="hrm-graph-stat__label">{"HR Below Low Threshold"}</span>
                                <span className="hrm-graph-stat__value">{`${modalData?.belowLowerThresholdCount}x <= ${modalData?.hrmSensorSetting?.lowHRThreshold} bpm`}</span>
                            </div>
                            <div className="hrm-graph-stat">
                                <span className="hrm-graph-stat__label">{"HR Above High Threshold"}</span>
                                <span className="hrm-graph-stat__value">{`${modalData?.aboveUpperThresholdCount}x >= ${modalData?.hrmSensorSetting?.highHRThreshold} bpm`}</span>
                            </div>
                        </div>
                    </>
                )}
                </div>
            </div>
        )
    }

    return (
        <div>
            <BootstrapTable
                keyField="id"
                wrapperClasses="drilldown-list-table-wrapper"
                classes="drilldown-list-table drilldown-list-table--hrm"
                data={props.hrmEventsList.sort((t1, t2) => {
                    if (t1.eventTimestamp > t2.eventTimestamp) return -1;
                    if (t1.eventTimestamp < t2.eventTimestamp) return 1;
                    return 0;
                })}
                columns={columns}
                striped
                hover
                condensed
                bordered={false}
            //defaultSorted={defaultSorted}
            />
            <GenericModal
                key="showgraph"
                className="genericModal hrm-graph-modal"
                showModal={showGraphModal}
                handleShowModal={handleShowGraphModal}
                fullscreen={isMobileView}
                ChildComponent={<NewHeartRateChart graphData={graphMomdalData} />}
            />
        </div>
    );
};

export default HRMList;
